/**
 * Message Intelligence Panel (MA-AN-129)
 * 
 * Displays message category performance for decision-making insights.
 * Shows which types of messaging perform best and where they succeed or fail.
 * 
 * This is a decision-making layer, not just a summary view.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Brain,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Users,
  Target,
  AlertTriangle,
  Trophy,
  Filter,
  ChevronDown,
  Zap,
  BarChart3,
  AlertCircle,
  CheckCircle2,
  XCircle,
  MessageSquare,
} from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { api } from '../../lib/api';

// =============================================================================
// API HOOKS
// =============================================================================

function useMessageCategoryPerformance({ period, sortBy, workspaceId, campaignId, vertical, channel, tenantId }) {
  return useQuery({
    queryKey: ['message-category-performance', period, sortBy, workspaceId, campaignId, vertical, channel, tenantId],
    queryFn: async () => {
      const params = new URLSearchParams({ period, sort_by: sortBy });
      if (workspaceId) params.append('workspace_id', workspaceId);
      if (campaignId && campaignId !== 'all') params.append('campaign_id', campaignId);
      if (vertical && vertical !== 'all') params.append('vertical', vertical);
      if (channel && channel !== 'all') params.append('channel', channel);
      if (tenantId) params.append('tenant_id', tenantId);
      
      const res = await api.get(`/analytics/message-category-performance?${params}`);
      return res.data;
    },
    staleTime: 60000,
    refetchOnWindowFocus: false,
  });
}

// =============================================================================
// FORMATTERS
// =============================================================================

function formatCurrency(value) {
  if (value === null || value === undefined) return '—';
  if (Math.abs(value) >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
  if (Math.abs(value) >= 1000) return `$${(value / 1000).toFixed(1)}K`;
  return `$${value.toFixed(0)}`;
}

function formatPercent(value) {
  if (value === null || value === undefined) return '—';
  return `${value.toFixed(1)}%`;
}

// =============================================================================
// INSIGHT CARDS
// =============================================================================

function InsightCard({ title, value, subtitle, icon: Icon, color = 'indigo', highlight = false }) {
  const colorClasses = {
    indigo: 'text-indigo-400 bg-indigo-500/10 border-indigo-500/20',
    green: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20',
    amber: 'text-amber-400 bg-amber-500/10 border-amber-500/20',
    red: 'text-red-400 bg-red-500/10 border-red-500/20',
    purple: 'text-purple-400 bg-purple-500/10 border-purple-500/20',
  };

  return (
    <div className={`rounded-lg p-4 border ${highlight ? 'ring-2 ring-offset-2 ring-offset-slate-900' : ''} ${colorClasses[color]}`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs text-slate-400 uppercase tracking-wide">{title}</p>
          <p className="text-lg font-bold text-white mt-1">{value || '—'}</p>
          {subtitle && (
            <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>
          )}
        </div>
        <Icon className={`w-5 h-5 ${colorClasses[color].split(' ')[0]}`} />
      </div>
    </div>
  );
}

// =============================================================================
// PERFORMANCE STATUS BADGE
// =============================================================================

function PerformanceStatusBadge({ status }) {
  const configs = {
    strong: {
      label: 'Strong',
      icon: CheckCircle2,
      className: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    },
    needs_work: {
      label: 'Needs Work',
      icon: AlertTriangle,
      className: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    },
    high_volume_low_conversion: {
      label: 'High Vol / Low Conv',
      icon: XCircle,
      className: 'bg-red-500/20 text-red-400 border-red-500/30',
    },
    neutral: {
      label: '',
      icon: null,
      className: '',
    },
  };

  const config = configs[status] || configs.neutral;
  
  if (!config.label) return null;
  
  const IconComponent = config.icon;
  
  return (
    <Badge variant="outline" className={`text-xs py-0.5 ${config.className}`}>
      {IconComponent && <IconComponent className="w-3 h-3 mr-1" />}
      {config.label}
    </Badge>
  );
}

// =============================================================================
// CATEGORY TABLE
// =============================================================================

function CategoryPerformanceTable({ categories, sortBy, onSort }) {
  const SortIcon = ({ field }) => {
    if (sortBy !== field) return null;
    return <ChevronDown className="w-3.5 h-3.5 inline ml-1" />;
  };

  const SortableHeader = ({ field, children, className = "" }) => (
    <TableHead 
      className={`text-slate-300 cursor-pointer hover:text-white transition-colors ${className}`}
      onClick={() => onSort(field)}
    >
      {children}
      <SortIcon field={field} />
    </TableHead>
  );

  if (categories.length === 0) {
    return (
      <div className="py-10 text-center" data-testid="category-empty">
        <Brain className="w-10 h-10 text-slate-600 mx-auto mb-3" />
        <h4 className="text-base font-medium text-slate-300 mb-1">No Category Data</h4>
        <p className="text-slate-500 text-sm max-w-sm mx-auto">
          Message category analytics appear when campaigns have message_category set.
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow className="border-slate-800 hover:bg-transparent">
            <TableHead className="text-slate-300 w-[160px]">Category</TableHead>
            <SortableHeader field="leads" className="text-right">Leads</SortableHeader>
            <TableHead className="text-right text-slate-300">Appts</TableHead>
            <TableHead className="text-right text-slate-300">Shows</TableHead>
            <TableHead className="text-right text-slate-300">Clients</TableHead>
            <SortableHeader field="revenue" className="text-right">Revenue</SortableHeader>
            <TableHead className="text-right text-slate-300">L→A</TableHead>
            <TableHead className="text-right text-slate-300">A→S</TableHead>
            <SortableHeader field="conversion" className="text-right">S→C</SortableHeader>
            <SortableHeader field="efficiency" className="text-right">Rev/Lead</SortableHeader>
            <TableHead className="text-center text-slate-300">Status</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {categories.map((cat) => (
            <TableRow 
              key={cat.message_category} 
              className={`border-slate-800/50 hover:bg-slate-800/30 ${
                cat.is_highest_revenue ? 'bg-emerald-500/5' : 
                cat.is_lowest_show_rate || cat.is_lowest_close_rate ? 'bg-amber-500/5' : ''
              }`}
              data-testid={`category-row-${cat.message_category}`}
            >
              <TableCell className="font-medium">
                <div className="flex items-center gap-2">
                  <span className="text-slate-100">{cat.category_label}</span>
                  {cat.is_highest_revenue && (
                    <Trophy className="w-3.5 h-3.5 text-emerald-400" title="Highest Revenue" />
                  )}
                  {cat.is_highest_conversion && (
                    <Zap className="w-3.5 h-3.5 text-purple-400" title="Best Conversion" />
                  )}
                  {(cat.is_lowest_show_rate || cat.is_lowest_close_rate) && (
                    <AlertTriangle className="w-3.5 h-3.5 text-amber-400" title="Needs Attention" />
                  )}
                </div>
              </TableCell>
              <TableCell className="text-right font-mono text-slate-100">{cat.leads}</TableCell>
              <TableCell className="text-right font-mono text-slate-100">{cat.appointments}</TableCell>
              <TableCell className="text-right font-mono text-slate-100">{cat.shows}</TableCell>
              <TableCell className="text-right font-mono text-slate-100">{cat.clients}</TableCell>
              <TableCell className="text-right font-mono text-emerald-400">{formatCurrency(cat.total_revenue)}</TableCell>
              <TableCell className="text-right font-mono text-slate-100">
                {formatPercent(cat.lead_to_appointment_rate)}
              </TableCell>
              <TableCell className="text-right font-mono text-slate-100">
                {formatPercent(cat.appointment_to_show_rate)}
              </TableCell>
              <TableCell className="text-right font-mono text-slate-100">
                {formatPercent(cat.show_to_close_rate)}
              </TableCell>
              <TableCell className="text-right font-mono text-slate-100">
                {formatCurrency(cat.revenue_per_lead)}
              </TableCell>
              <TableCell className="text-center">
                <PerformanceStatusBadge status={cat.performance_status} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export default function MessageIntelligence({ 
  period = 'mtd', 
  workspaceId, 
  campaignId,
  vertical,
  channel,
  tenantId 
}) {
  const [sortBy, setSortBy] = useState('revenue');
  
  const query = useMessageCategoryPerformance({
    period,
    sortBy,
    workspaceId,
    campaignId,
    vertical,
    channel,
    tenantId,
  });

  const categories = query.data?.categories || [];
  const insights = query.data?.insights || {};

  return (
    <div className="space-y-4" data-testid="message-intelligence">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Brain className="w-5 h-5 text-purple-400" />
            Message Intelligence
          </h3>
          <p className="text-sm text-slate-400 mt-1">
            Category-level performance insights for messaging optimization
          </p>
        </div>
      </div>

      {/* Insight Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <InsightCard
          title="Top Performing Category"
          value={insights.highest_revenue_category ? 
            (MESSAGE_CATEGORY_LABELS[insights.highest_revenue_category] || insights.highest_revenue_category) : null}
          subtitle={insights.highest_revenue_amount ? formatCurrency(insights.highest_revenue_amount) + ' revenue' : null}
          icon={Trophy}
          color="green"
          highlight={!!insights.highest_revenue_category}
        />
        <InsightCard
          title="Best Conversion"
          value={insights.best_conversion_category ? 
            (MESSAGE_CATEGORY_LABELS[insights.best_conversion_category] || insights.best_conversion_category) : null}
          subtitle={insights.best_conversion_rate ? formatPercent(insights.best_conversion_rate) + ' close rate' : null}
          icon={TrendingUp}
          color="purple"
        />
        <InsightCard
          title="Needs Attention"
          value={insights.weakest_category ? 
            (MESSAGE_CATEGORY_LABELS[insights.weakest_category] || insights.weakest_category) : null}
          subtitle={insights.weakest_issue || null}
          icon={AlertTriangle}
          color="amber"
        />
      </div>

      {/* Summary Stats */}
      <div className="flex items-center gap-6 text-sm text-slate-400">
        <span className="flex items-center gap-1.5">
          <BarChart3 className="w-4 h-4" />
          <strong className="text-slate-200">{query.data?.total_categories || 0}</strong> categories
        </span>
        <span className="flex items-center gap-1.5">
          <Users className="w-4 h-4" />
          <strong className="text-slate-200">{query.data?.total_leads || 0}</strong> leads
        </span>
        <span className="flex items-center gap-1.5">
          <DollarSign className="w-4 h-4" />
          <strong className="text-emerald-400">{formatCurrency(query.data?.total_revenue || 0)}</strong> revenue
        </span>
      </div>

      {/* Sort Control */}
      <div className="flex justify-end">
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-44 bg-slate-800 border-slate-700 h-8 text-sm">
            <Filter className="w-3.5 h-3.5 mr-2 text-slate-400" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="revenue">Revenue</SelectItem>
            <SelectItem value="leads">Leads</SelectItem>
            <SelectItem value="conversion">Close Rate</SelectItem>
            <SelectItem value="efficiency">Revenue/Lead</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Performance Table */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardContent className="p-0">
          {query.isLoading ? (
            <div className="py-10 text-center text-slate-400">
              <div className="animate-pulse">Loading message categories...</div>
            </div>
          ) : query.error ? (
            <div className="py-10 text-center">
              <MessageSquare className="w-10 h-10 text-slate-600 mx-auto mb-3" />
              <p className="text-slate-300 font-medium text-sm">Message Intelligence Unavailable</p>
              <p className="text-slate-500 text-xs mt-1">Unable to load message category data at this time.</p>
            </div>
          ) : (
            <CategoryPerformanceTable 
              categories={categories} 
              sortBy={sortBy}
              onSort={setSortBy}
            />
          )}
        </CardContent>
      </Card>

      {/* Data Note */}
      <div className="text-xs text-slate-500 text-center">
        * Based on campaigns with message_category. Records without category are excluded.
      </div>
    </div>
  );
}

// Category labels for display
const MESSAGE_CATEGORY_LABELS = {
  cost_savings: "Cost Savings",
  income: "Income",
  protection: "Protection",
  growth: "Growth",
  convenience: "Convenience",
  certainty: "Certainty",
  opportunity: "Opportunity",
  relief: "Relief",
  speed: "Speed",
  status: "Status",
  other: "Other",
};
